import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Clock, Users, Star, BookOpen, Play, 
  CheckCircle2, Lock, ChevronDown, ChevronUp 
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Course } from '../types/database'

interface Module {
  id: string
  title: string
  description: string
  order_index: number
  duration_minutes: number
  is_published: boolean
  lessons: Lesson[]
}

interface Lesson {
  id: string
  title: string
  content: string
  order_index: number
  duration_minutes: number
  video_url: string | null
  module_id: string
}

interface UserProgress {
  completed_lessons: string[]
  current_lesson_id: string | null
}

interface CoursePageProps {
  user: any
}

export default function CoursePage({ user }: CoursePageProps) {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const [course, setCourse] = useState<Course | null>(null)
  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState(true)
  const [enrolled, setEnrolled] = useState(false)
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set())
  const [userProgress, setUserProgress] = useState<UserProgress>({
    completed_lessons: [],
    current_lesson_id: null
  })

  useEffect(() => {
    if (courseId) {
      fetchCourseData()
      if (user) {
        checkEnrollment()
        fetchUserProgress()
      }
    }
  }, [courseId, user])

  const fetchCourseData = async () => {
    try {
      // Fetch course details
      const { data: courseData } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single()

      if (courseData) {
        setCourse(courseData)
      }

      // Fetch modules with lessons
      const { data: modulesData } = await supabase
        .from('course_modules')
        .select(`
          id,
          title,
          description,
          order_index,
          duration_minutes,
          is_published
        `)
        .eq('course_id', courseId)
        .eq('is_published', true)
        .order('order_index', { ascending: true })

      if (modulesData) {
        // Fetch lessons for each module
        const modulesWithLessons = await Promise.all(
          modulesData.map(async (module) => {
            const { data: lessonsData } = await supabase
              .from('course_lessons')
              .select('*')
              .eq('module_id', module.id)
              .order('order_index', { ascending: true })

            return {
              ...module,
              lessons: lessonsData || []
            }
          })
        )

        setModules(modulesWithLessons)
        // Expand first module by default
        if (modulesWithLessons.length > 0) {
          setExpandedModules(new Set([modulesWithLessons[0].id]))
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error)
    } finally {
      setLoading(false)
    }
  }

  const checkEnrollment = async () => {
    if (!user) return

    const { data } = await supabase
      .from('user_course_progress')
      .select('id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .single()

    setEnrolled(!!data)
  }

  const fetchUserProgress = async () => {
    if (!user) return

    const { data } = await supabase
      .from('user_course_progress')
      .select('completed_lessons, current_lesson_id')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .single()

    if (data) {
      // Parse JSONB array if needed
      let completedLessons = []
      if (data.completed_lessons) {
        completedLessons = Array.isArray(data.completed_lessons) 
          ? data.completed_lessons 
          : JSON.parse(JSON.stringify(data.completed_lessons))
      }
      setUserProgress({
        completed_lessons: completedLessons,
        current_lesson_id: data.current_lesson_id
      })
    }
  }

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login')
      return
    }

    try {
      const { error } = await supabase
        .from('user_course_progress')
        .insert({
          user_id: user.id,
          course_id: courseId,
          progress_percentage: 0,
          completed_lessons: [],
          started_at: new Date().toISOString(),
          status: 'in_progress'
        })

      if (!error) {
        setEnrolled(true)
        await fetchUserProgress()
        alert('¡Te has inscrito exitosamente en el curso!')
      } else {
        console.error('Error enrolling:', error)
        alert('Error al inscribirse: ' + error.message)
      }
    } catch (error) {
      console.error('Error enrolling:', error)
      alert('Error al inscribirse en el curso')
    }
  }

  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }

  const isLessonCompleted = (lessonId: string) => {
    if (!userProgress.completed_lessons) return false
    // Handle both array and JSONB array formats
    const lessons = Array.isArray(userProgress.completed_lessons) 
      ? userProgress.completed_lessons 
      : []
    return lessons.includes(lessonId)
  }

  const getTotalLessons = () => {
    return modules.reduce((total, module) => total + module.lessons.length, 0)
  }

  const getCompletedLessons = () => {
    if (!userProgress.completed_lessons) return 0
    const lessons = Array.isArray(userProgress.completed_lessons) 
      ? userProgress.completed_lessons 
      : []
    return lessons.length
  }

  const getProgressPercentage = () => {
    const total = getTotalLessons()
    if (total === 0) return 0
    return Math.round((getCompletedLessons() / total) * 100)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-education"></div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-800 mb-2">Curso no encontrado</h2>
          <Link to="/cursos" className="text-education hover:underline">
            Volver a cursos
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header del curso */}
      <div className="bg-gradient-to-r from-education to-education-dark text-white">
        <div className="container mx-auto px-4 py-12">
          <Link to="/cursos" className="inline-flex items-center text-white hover:text-neutral-200 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a cursos
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm capitalize">
                  {course.level}
                </span>
                <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm">
                  {course.theme}
                </span>
              </div>

              <h1 className="font-heading font-bold text-4xl mb-4">
                {course.title}
              </h1>

              <p className="text-xl text-neutral-100 mb-6">
                {course.description}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 fill-yellow-400 text-yellow-400 mr-2" />
                  <span className="font-semibold">{course.rating.toFixed(1)}</span>
                  <span className="ml-1 text-neutral-200">({course.total_students} estudiantes)</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>{course.duration_hours} horas</span>
                </div>
                <div className="flex items-center">
                  <BookOpen className="w-5 h-5 mr-2" />
                  <span>{modules.length} módulos</span>
                </div>
                <div className="flex items-center">
                  <Play className="w-5 h-5 mr-2" />
                  <span>{getTotalLessons()} lecciones</span>
                </div>
              </div>

              {course.instructor_name && (
                <div className="mt-6 pt-6 border-t border-white border-opacity-20">
                  <p className="text-sm text-neutral-200">Instructor</p>
                  <p className="font-semibold">{course.instructor_name}</p>
                </div>
              )}
            </div>

            {/* Sidebar de inscripción */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 text-neutral-800">
                {enrolled ? (
                  <>
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-neutral-600">Tu progreso</span>
                        <span className="text-sm font-bold text-education">{getProgressPercentage()}%</span>
                      </div>
                      <div className="w-full bg-neutral-200 rounded-full h-2">
                        <div 
                          className="bg-education h-2 rounded-full transition-all duration-300"
                          style={{ width: `${getProgressPercentage()}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">
                        {getCompletedLessons()} de {getTotalLessons()} lecciones completadas
                      </p>
                    </div>
                    <button 
                      onClick={() => {
                        const firstModule = modules[0]
                        if (firstModule && firstModule.lessons.length > 0) {
                          navigate(`/cursos/${courseId}/leccion/${firstModule.lessons[0].id}`)
                        }
                      }}
                      className="w-full bg-action text-white py-3 rounded-lg font-medium hover:bg-action-dark transition-colors flex items-center justify-center"
                    >
                      <Play className="w-5 h-5 mr-2" />
                      Continuar aprendiendo
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-center mb-6">
                      <p className="text-2xl font-bold text-neutral-800 mb-2">Gratis</p>
                      <p className="text-sm text-neutral-600">Acceso completo al curso</p>
                    </div>
                    <button 
                      onClick={handleEnroll}
                      className="w-full bg-education text-white py-3 rounded-lg font-medium hover:bg-education-dark transition-colors"
                    >
                      Inscribirse ahora
                    </button>
                    <p className="text-xs text-neutral-500 text-center mt-4">
                      Acceso ilimitado • Certificado al finalizar
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido del curso */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contenido principal */}
          <div className="lg:col-span-2">
            <h2 className="font-heading font-bold text-2xl text-neutral-800 mb-6">
              Contenido del curso
            </h2>

            <div className="space-y-4">
              {modules.map((module) => (
                <div key={module.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <button
                    onClick={() => toggleModule(module.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
                  >
                    <div className="flex-1 text-left">
                      <h3 className="font-semibold text-lg text-neutral-800 mb-1">
                        {module.title}
                      </h3>
                      <p className="text-sm text-neutral-600">
                        {module.lessons.length} lecciones • {module.duration_minutes} min
                      </p>
                    </div>
                    {expandedModules.has(module.id) ? (
                      <ChevronUp className="w-5 h-5 text-neutral-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-neutral-400" />
                    )}
                  </button>

                  {expandedModules.has(module.id) && (
                    <div className="border-t border-neutral-200">
                      {module.lessons.map((lesson) => (
                        <Link
                          key={lesson.id}
                          to={enrolled ? `/cursos/${courseId}/leccion/${lesson.id}` : '#'}
                          onClick={(e) => !enrolled && e.preventDefault()}
                          className={`flex items-center px-6 py-3 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 ${
                            !enrolled ? 'cursor-not-allowed opacity-60' : ''
                          }`}
                        >
                          <div className="flex-shrink-0 mr-3">
                            {enrolled && isLessonCompleted(lesson.id) ? (
                              <CheckCircle2 className="w-5 h-5 text-action" />
                            ) : enrolled ? (
                              <Play className="w-5 h-5 text-education" />
                            ) : (
                              <Lock className="w-5 h-5 text-neutral-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-neutral-800">
                              {lesson.title}
                            </p>
                          </div>
                          <div className="text-xs text-neutral-500">
                            {lesson.duration_minutes} min
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h3 className="font-semibold text-lg mb-4">Lo que aprenderás</h3>
              <ul className="space-y-3 text-sm text-neutral-700">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-action mr-2 flex-shrink-0 mt-0.5" />
                  <span>Comprensión profunda del medio ambiente y su importancia</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-action mr-2 flex-shrink-0 mt-0.5" />
                  <span>Análisis de los principales desafíos ambientales globales</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-action mr-2 flex-shrink-0 mt-0.5" />
                  <span>Soluciones específicas para tu región</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-action mr-2 flex-shrink-0 mt-0.5" />
                  <span>Acciones prácticas que puedes implementar hoy</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-action mr-2 flex-shrink-0 mt-0.5" />
                  <span>Recursos y referencias científicas actualizadas</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
